import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from './api';

export interface Me { email: string; isAdmin: boolean }

let cached: Me | null | undefined; // undefined = unknown
const listeners = new Set<(me: Me | null | undefined) => void>();

function setCached(me: Me | null | undefined): void {
  cached = me;
  listeners.forEach((listener) => listener(me));
}

/** How long to wait before asking again when the server couldn't be reached. */
const RETRY_DELAY_MS = 1000;
let loading: Promise<void> | undefined;

async function ask(): Promise<void> {
  try {
    setCached(await api.get<Me>('/me'));
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) setCached(null);
    else throw e;
  }
}

/**
 * Asks who is signed in. A request that never reaches the server, as when a phone switches networks or wakes a tab, is
 * tried once more; if that fails too, the answer stays unknown until the tab is shown again. `reportingFetch` has
 * already logged those failures.
 */
function load(): Promise<void> {
  loading ??= (async () => {
    try {
      await ask();
    } catch (e) {
      if (e instanceof ApiError) throw e;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      await ask().catch((retryError) => { if (retryError instanceof ApiError) throw retryError; });
    } finally {
      loading = undefined;
    }
  })();
  return loading;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && cached === undefined && listeners.size > 0) void load();
  });
}

export function useMe(): { me: Me | null | undefined; refresh: () => Promise<void> } {
  const [me, setMe] = useState<Me | null | undefined>(cached);
  useEffect(() => {
    listeners.add(setMe);
    setMe(cached);
    if (cached === undefined) void load();
    return () => { listeners.delete(setMe); };
  }, []);
  return { me, refresh: load };
}

/** Clears the cached signed-in user (after the server has ended the session). */
export function forgetMe(): void {
  setCached(null);
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
  setCached(null);
}
